import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Scale,
  Newspaper,
  Filter,
  Eye,
  Bookmark,
  Bell,
  ArrowRight,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LegislationTracker from "@/components/LegislationTracker";
import LegislationNews from "@/components/LegislationNews";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

const pipelineSteps = [
  {
    icon: Newspaper,
    label: "We watch the sources",
    body: "Bills introduced in Montgomery, plus any federal rules that hit Alabama. The same places reporters and lobbyists check — pulled into one place.",
  },
  {
    icon: Filter,
    label: "We sort what matters",
    body: "Each bill gets tagged by topic (housing, health, climate, justice, education) and county so you only see what's relevant to your work.",
  },
  {
    icon: Eye,
    label: "We track what changes",
    body: "When a bill moves — introduced, in committee, passed, signed — we update its status so you don't have to refresh ALISON yourself.",
  },
  {
    icon: Bookmark,
    label: "You see it here",
    body: "Search, filter, and click any bill to read the plain-language summary, sponsors, and where it's headed next.",
  },
];

const LegislationPipeline = () => {
  const { data: stats } = useQuery({
    queryKey: ["legislation", "coverage-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("county, status, date");
      if (error) throw error;
      const counties = new Set(
        (data ?? []).map((b) => b.county).filter(Boolean) as string[],
      );
      const active = (data ?? []).filter(
        (b) => b.status && b.status !== "Failed" && b.status !== "Enacted",
      ).length;
      const lastUpdated = (data ?? [])
        .map((b) => (b.date ? new Date(b.date).getTime() : 0))
        .reduce((max, t) => (t > max ? t : max), 0);
      return {
        total: data?.length ?? 0,
        active,
        counties: counties.size,
        lastUpdated: lastUpdated ? new Date(lastUpdated) : null,
      };
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(135 30% 45%) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary/30 bg-primary/5 text-primary">
                <Scale className="h-4 w-4" />
              </span>
              <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">
                Legislation Pipeline
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5">
              Alabama legislation,{" "}
              <span className="text-gradient-primary">in plain language</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              We follow the bills moving through Montgomery so you don't have to. Search by topic, county, or status — and click any bill to see what it actually does, who's behind it, and where it stands today.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#tracker"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Browse the bills <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/coach"
                className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-primary/40 transition-colors"
              >
                <Bell className="h-4 w-4" /> Get bill alerts
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How the pipeline works */}
      <section className="py-16 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              How it works
            </span>
            <h2 className="font-display text-3xl font-bold mt-2">
              From the statehouse to your screen
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              No legalese, no PDF hunts. Here's the simple path a bill takes before it lands in the tracker below.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {pipelineSteps.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <step.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Step {i + 1}
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">
                  {step.label}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage stats */}
      <section className="py-12 bg-muted/20 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Bills tracked", value: stats?.total ?? "—" },
              { label: "Active this session", value: stats?.active ?? "—" },
              { label: "Counties covered", value: stats?.counties ?? "—" },
              {
                label: "Last bill update",
                value: stats?.lastUpdated
                  ? format(stats.lastUpdated, "MMM d, yyyy")
                  : "—",
              },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-card border border-border rounded-xl p-5"
              >
                <div className="font-display text-2xl md:text-3xl font-bold">
                  {s.value}
                </div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live tracker */}
      <div id="tracker">
        <LegislationTracker />
      </div>

      {/* Latest legislation news */}
      <LegislationNews
        eyebrow="In the news"
        heading="What reporters are saying"
        description="Headlines from Alabama newsrooms covering the bills above. When a story mentions a bill we're tracking, you'll see its ID tagged on the card."
        showPipelineLink={false}
      />

      <SiteFooter />
    </div>
  );
};

export default LegislationPipeline;